import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';
import { clearCart } from './cartSlice';
import { selectUser } from './authSlice';

// Initial state
const initialState = {
  // User orders
  userOrders: [],
  userOrdersLoading: false,
  userOrdersError: null,
  
  // Current order (for order details)
  currentOrder: null,
  currentOrderLoading: false,
  currentOrderError: null,
  
  // Admin orders
  adminOrders: [],
  adminOrdersLoading: false,
  adminOrdersError: null,
  
  // Order statistics (admin)
  orderStats: {
    totalOrders: 0,
    totalDelivered: 0,
    loading: false,
  },
  
  // Order creation
  creatingOrder: false,
  createOrderError: null,
  
  // Filters and pagination
  filters: {
    status: 'all',
    searchTerm: '',
  },
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
  },
};

// Async thunks
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        userId,
        items = [],
        shippingCost = 0,
        // taxAmount removed - backend calculates it from system settings
        discountAmount = 0,
        paymentMethod = 'unknown',
        paymentStatus = 'pending',
        shippingAddress,
        status = 'Pending',
        notes = null,
      } = orderData;

      const response = await axios.post('/orders/create', {
        userId,
        items,
        shippingCost,
        // taxAmount is now calculated on backend - don't send it
        discountAmount,
        paymentMethod,
        paymentStatus,
        shippingAddress,
        status,
        notes,
      });

      // Clear cart after successful order creation (both localStorage and database)
      const state = getState();
      const user = selectUser(state);
      dispatch(clearCart(user));
      
      // Show success message
      const message = response.data.message || 'Order created successfully!';
      toast.success(message);
      
      return response.data.data || response.data;
    } catch (error) {
      // Get detailed error message
      let message = 'Failed to create order. Please try again.';
      
      if (error.response?.data) {
        // Check for validation errors
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map(err => 
            err.message || err.msg || (typeof err === 'string' ? err : JSON.stringify(err))
          ).join(', ');
          message = `Validation error: ${errorMessages}`;
        } else if (error.response.data.message) {
          message = typeof error.response.data.message === 'string' 
            ? error.response.data.message 
            : JSON.stringify(error.response.data.message);
        } else if (error.response.data.error) {
          message = typeof error.response.data.error === 'string' 
            ? error.response.data.error 
            : JSON.stringify(error.response.data.error);
        }
      } else if (error.message) {
        message = typeof error.message === 'string' 
          ? error.message 
          : JSON.stringify(error.message);
      }
      
      console.error('Order creation error:', {
        status: error.response?.status,
        data: error.response?.data,
        message
      });
      
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getUserOrders = createAsyncThunk(
  'orders/getUserOrders',
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get('/orders/my-orders', {
        params: { page, limit },
      });
      
      // Handle new API response structure
      if (response.data.success && response.data.data) {
        return {
          orders: response.data.data || [],
          pagination: response.data.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
          },
        };
      }
      
      // Fallback for old API structure
      return {
        orders: response.data.orders || [],
        pagination: {
          page: 1,
          limit: 20,
          total: (response.data.orders || []).length,
          totalPages: 1,
        },
      };
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : typeof error.response?.data?.error?.message === 'string'
          ? error.response?.data?.error?.message
          : 'Failed to fetch your orders.';
      return rejectWithValue(message);
    }
  }
);

export const getOrderById = createAsyncThunk(
  'orders/getOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/orders/${orderId}`);
      
      // Handle new API response structure
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      // Fallback for old API structure
      return response.data.data || response.data;
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : typeof error.response?.data?.error?.message === 'string'
          ? error.response?.data?.error?.message
          : 'Failed to fetch order details.';
      return rejectWithValue(message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (orderId, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`/orders/cancel/${orderId}`);
      toast.success('Order cancelled successfully.');
      return { orderId, order: response.data };
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to cancel order.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const reorderItems = createAsyncThunk(
  'orders/reorderItems',
  async ({ orderId, reorderData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/orders/reorder/${orderId}`, reorderData);
      toast.success('Items added to cart successfully.');
      return response.data;
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to reorder items.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const requestReturn = createAsyncThunk(
  'orders/requestReturn',
  async ({ orderId, returnData, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      
      // Add text fields
      formData.append('reason', returnData.reason || '');
      if (returnData.itemsToReturn && Array.isArray(returnData.itemsToReturn)) {
        formData.append('itemsToReturn', JSON.stringify(returnData.itemsToReturn));
      }
      
      // Add image files
      if (images && Array.isArray(images)) {
        images.forEach((file) => {
          formData.append('images', file);
        });
      }
      
      const response = await axios.post(`/orders/return/${orderId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Return request submitted successfully.');
      return response.data;
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to submit return request.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Admin thunks
export const getAllOrders = createAsyncThunk(
  'orders/getAllOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/orders/all-orders');
      // Handle new API response structure
      if (response.data.success && response.data.data) {
        return response.data.data || [];
      }
      // Fallback for old API structure
      return response.data.orders || [];
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to fetch all orders.';
      return rejectWithValue(message);
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ orderId, status }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`/orders/update-status/${orderId}`, { status });
      toast.success('Order status updated successfully.');
      return { orderId, status, order: response.data };
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to update order status.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getTotalOrders = createAsyncThunk(
  'orders/getTotalOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/orders/total');
      return response.data.totalOrders || 0;
    } catch (error) {
      return rejectWithValue(
        typeof error.response?.data?.error?.message === 'string'
          ? error.response?.data?.error?.message
          : 'Failed to fetch total orders count.'
      );
    }
  }
);

export const getDeliveredOrders = createAsyncThunk(
  'orders/getDeliveredOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/orders/delivered');
      return response.data.deliveredOrders || [];
    } catch (error) {
      return rejectWithValue(
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to fetch delivered orders.'
      );
    }
  }
);

export const getTotalDeliveredOrders = createAsyncThunk(
  'orders/getTotalDeliveredOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/orders/delivered/total');
      return response.data.totalDelivered || 0;
    } catch (error) {
      return rejectWithValue(
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to fetch total delivered orders count.'
      );
    }
  }
);

// Admin create order on behalf of user
export const createAdminOrder = createAsyncThunk(
  'orders/createAdminOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/orders/admin/create', orderData);
      
      const message = response.data.message || 'Order created successfully!';
      toast.success(message);
      
      return response.data.data || response.data;
    } catch (error) {
      let message = 'Failed to create order. Please try again.';
      
      if (error.response?.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map(err => 
            err.message || err.msg || (typeof err === 'string' ? err : JSON.stringify(err))
          ).join(', ');
          message = `Validation error: ${errorMessages}`;
        } else if (error.response.data.message) {
          message = typeof error.response.data.message === 'string' 
            ? error.response.data.message 
            : JSON.stringify(error.response.data.message);
        } else if (error.response.data.error) {
          message = typeof error.response.data.error === 'string' 
            ? error.response.data.error 
            : JSON.stringify(error.response.data.error);
        }
      } else if (error.message) {
        message = error.message;
      }
      
      console.error('Admin order creation error:', {
        status: error.response?.status,
        data: error.response?.data,
        message
      });
      
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Admin mark order as paid
export const markOrderAsPaid = createAsyncThunk(
  'orders/markOrderAsPaid',
  async ({ orderId, transactionId, paymentMethod, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/orders/admin/${orderId}/mark-paid`, {
        transactionId,
        paymentMethod,
        notes,
      });
      
      toast.success('Order marked as paid successfully!');
      return { orderId, ...response.data.data };
    } catch (error) {
      const message =
        typeof error.response?.data?.message === 'string'
          ? error.response?.data?.message
          : 'Failed to mark order as paid.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Order slice
const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // Filters
    setStatusFilter: (state, action) => {
      // Convert empty string to "all" for consistency
      state.filters.status = action.payload === "" ? "all" : action.payload;
      state.pagination.currentPage = 1;
    },
    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        searchTerm: '',
      };
      state.pagination.currentPage = 1;
    },
    
    // Pagination
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.itemsPerPage = action.payload;
      state.pagination.currentPage = 1;
    },
    
    // Clear current order
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderError = null;
    },
  },
  extraReducers: (builder) => {
    // Create order
    builder
      .addCase(createOrder.pending, (state) => {
        state.creatingOrder = true;
        state.createOrderError = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.creatingOrder = false;
        state.createOrderError = null;
        // The order will be fetched from the server when needed
        // We don't add it here since we only have orderId, not full order data
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.creatingOrder = false;
        state.createOrderError = action.payload;
      });

    // Get user orders
    builder
      .addCase(getUserOrders.pending, (state) => {
        state.userOrdersLoading = true;
        state.userOrdersError = null;
      })
      .addCase(getUserOrders.fulfilled, (state, action) => {
        state.userOrdersLoading = false;
        state.userOrders = action.payload.orders || action.payload || [];
        // Update pagination if provided
        if (action.payload.pagination) {
          state.pagination = {
            ...state.pagination,
            currentPage: action.payload.pagination.page || state.pagination.currentPage,
            itemsPerPage: action.payload.pagination.limit || state.pagination.itemsPerPage,
            total: action.payload.pagination.total || 0,
            totalPages: action.payload.pagination.totalPages || Math.ceil((action.payload.orders || []).length / state.pagination.itemsPerPage),
          };
        }
        state.userOrdersError = null;
      })
      .addCase(getUserOrders.rejected, (state, action) => {
        state.userOrdersLoading = false;
        state.userOrdersError = action.payload;
        state.userOrders = [];
      });

    // Get order by ID
    builder
      .addCase(getOrderById.pending, (state) => {
        state.currentOrderLoading = true;
        state.currentOrderError = null;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.currentOrderLoading = false;
        state.currentOrder = action.payload;
        state.currentOrderError = null;
      })
      .addCase(getOrderById.rejected, (state, action) => {
        state.currentOrderLoading = false;
        state.currentOrderError = action.payload;
        state.currentOrder = null;
      });

    // Cancel order
    builder
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const { orderId, order } = action.payload;
        // Update in user orders
        const userOrderIndex = state.userOrders.findIndex(
          (o) => o.order_id === orderId || o.id === orderId
        );
        if (userOrderIndex !== -1) {
          state.userOrders[userOrderIndex] = { ...state.userOrders[userOrderIndex], ...order };
        }
        // Update current order if it's the same
        if (state.currentOrder && 
            (state.currentOrder.order_id === orderId || state.currentOrder.id === orderId)) {
          state.currentOrder = { ...state.currentOrder, ...order };
        }
        // Update in admin orders
        const adminOrderIndex = state.adminOrders.findIndex(
          (o) => (o.order_id || o.id) === orderId
        );
        if (adminOrderIndex !== -1) {
          state.adminOrders[adminOrderIndex] = { ...state.adminOrders[adminOrderIndex], ...order };
        }
      });

    // Request return
    builder
      .addCase(requestReturn.fulfilled, (state, action) => {
        // Update current order if it exists
        if (state.currentOrder) {
          if (!state.currentOrder.returns) {
            state.currentOrder.returns = [];
          }
          state.currentOrder.returns.push(action.payload);
        }
      });

    // Get all orders (admin)
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.adminOrdersLoading = true;
        state.adminOrdersError = null;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.adminOrdersLoading = false;
        state.adminOrders = action.payload;
        state.pagination.total = action.payload.length;
        state.adminOrdersError = null;
      })
      .addCase(getAllOrders.rejected, (state, action) => {
        state.adminOrdersLoading = false;
        state.adminOrdersError = action.payload;
        state.adminOrders = [];
      });

    // Update order status (admin)
    builder
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const { orderId, status, order } = action.payload;
        // Update in admin orders
        const adminOrderIndex = state.adminOrders.findIndex(
          (o) => (o.order_id || o.id) === orderId
        );
        if (adminOrderIndex !== -1) {
          state.adminOrders[adminOrderIndex] = {
            ...state.adminOrders[adminOrderIndex],
            status,
            ...order,
          };
        }
        // Update in user orders
        const userOrderIndex = state.userOrders.findIndex(
          (o) => o.order_id === orderId || o.id === orderId
        );
        if (userOrderIndex !== -1) {
          state.userOrders[userOrderIndex] = {
            ...state.userOrders[userOrderIndex],
            status,
            ...order,
          };
        }
        // Update current order if it's the same
        if (state.currentOrder && 
            (state.currentOrder.order_id === orderId || state.currentOrder.id === orderId)) {
          state.currentOrder = { ...state.currentOrder, status, ...order };
        }
      });

    // Get total orders
    builder
      .addCase(getTotalOrders.pending, (state) => {
        state.orderStats.loading = true;
      })
      .addCase(getTotalOrders.fulfilled, (state, action) => {
        state.orderStats.totalOrders = action.payload;
        state.orderStats.loading = false;
      })
      .addCase(getTotalOrders.rejected, (state) => {
        state.orderStats.loading = false;
      });

    // Get total delivered orders
    builder
      .addCase(getTotalDeliveredOrders.fulfilled, (state, action) => {
        state.orderStats.totalDelivered = action.payload;
      });

    // Admin create order
    builder
      .addCase(createAdminOrder.pending, (state) => {
        state.creatingOrder = true;
        state.createOrderError = null;
      })
      .addCase(createAdminOrder.fulfilled, (state, action) => {
        state.creatingOrder = false;
        state.createOrderError = null;
        // Add the new order to adminOrders if we have it
        if (action.payload) {
          state.adminOrders.unshift(action.payload);
        }
      })
      .addCase(createAdminOrder.rejected, (state, action) => {
        state.creatingOrder = false;
        state.createOrderError = action.payload;
      });

    // Mark order as paid
    builder
      .addCase(markOrderAsPaid.fulfilled, (state, action) => {
        const { orderId } = action.payload;
        // Update in admin orders
        const adminOrderIndex = state.adminOrders.findIndex(
          (o) => (o.order_id || o.orderId) === orderId
        );
        if (adminOrderIndex !== -1) {
          state.adminOrders[adminOrderIndex] = {
            ...state.adminOrders[adminOrderIndex],
            payment_status: 'paid',
            paymentStatus: 'paid',
          };
        }
        // Update current order if it's the same
        if (state.currentOrder && 
            (state.currentOrder.order_id === orderId || state.currentOrder.orderId === orderId)) {
          state.currentOrder = { 
            ...state.currentOrder, 
            payment_status: 'paid',
            paymentStatus: 'paid',
          };
        }
      });
  },
});

export const {
  setStatusFilter,
  setSearchTerm,
  clearFilters,
  setCurrentPage,
  setItemsPerPage,
  clearCurrentOrder,
} = orderSlice.actions;

// Selectors
export const selectUserOrders = (state) => state.orders.userOrders;
export const selectUserOrdersLoading = (state) => state.orders.userOrdersLoading;
export const selectUserOrdersError = (state) => state.orders.userOrdersError;

export const selectCurrentOrder = (state) => state.orders.currentOrder;
export const selectCurrentOrderLoading = (state) => state.orders.currentOrderLoading;
export const selectCurrentOrderError = (state) => state.orders.currentOrderError;

export const selectAdminOrders = (state) => state.orders.adminOrders;
export const selectAdminOrdersLoading = (state) => state.orders.adminOrdersLoading;
export const selectAdminOrdersError = (state) => state.orders.adminOrdersError;

export const selectOrderStats = (state) => state.orders.orderStats;
export const selectCreatingOrder = (state) => state.orders.creatingOrder;
export const selectCreateOrderError = (state) => state.orders.createOrderError;

export const selectOrderFilters = (state) => state.orders.filters;
export const selectOrderPagination = (state) => state.orders.pagination;

// Computed selectors
export const selectFilteredUserOrders = createSelector(
  [selectUserOrders, selectOrderFilters],
  (orders, filters) => {
    const { status, searchTerm } = filters;

    return orders.filter((order) => {
      // Status filter - skip if status is "all" or empty
      if (status && status !== "all" && order.status !== status) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch =
          order.order_id?.toLowerCase().includes(lower) ||
          order.id?.toLowerCase().includes(lower) ||
          order.status?.toLowerCase().includes(lower);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }
);

// Memoized selectors using createSelector
export const selectFilteredAdminOrders = createSelector(
  [selectAdminOrders, selectOrderFilters],
  (orders, filters) => {
    const { status, searchTerm } = filters;

    return orders.filter((order) => {
      // Status filter - skip if status is "all" or empty
      if (status && status !== "all" && order.status !== status) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch =
          (order.order_id || order.id)?.toLowerCase().includes(lower) ||
          order.customer?.toLowerCase().includes(lower) ||
          order.status?.toLowerCase().includes(lower);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }
);

export const selectPaginatedUserOrders = createSelector(
  [selectFilteredUserOrders, selectOrderPagination],
  (filtered, pagination) => {
    const { currentPage, itemsPerPage } = pagination;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }
);

export const selectPaginatedAdminOrders = createSelector(
  [selectFilteredAdminOrders, selectOrderPagination],
  (filtered, pagination) => {
    const { currentPage, itemsPerPage } = pagination;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }
);

export const selectUserOrdersTotalPages = createSelector(
  [selectFilteredUserOrders, selectOrderPagination],
  (filtered, pagination) => {
    return Math.ceil(filtered.length / pagination.itemsPerPage);
  }
);

export const selectAdminOrdersTotalPages = createSelector(
  [selectFilteredAdminOrders, selectOrderPagination],
  (filtered, pagination) => {
    return Math.ceil(filtered.length / pagination.itemsPerPage);
  }
);

export default orderSlice.reducer;

